import { formatNumber } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import {
  API_PREFIX_V2,
  GlobalWebsocketService
} from '@approot/global-websocket';
import { AlertsService, LegService } from '@approot/services';
import {
  AgGridColorPalette,
  exponentialDelay,
  shortUuid,
  sleep,
  SubscriptionSink,
  uniq
} from '@approot/utilities';
import { LegComponent } from '@approot/widgets';
import { AgChartOptions } from 'ag-charts-community';
import copy from 'copy-to-clipboard';

type Message = {
  info: boolean;
  error: boolean;
  warning: boolean;
  message: string;
};

type Rates = {
  reportingCurrency: string;
  prices: Record<string, number[]>;
  ratesBumps: number[];
};

type Status = 'calculating' | 'finished' | null;
type CopyState = 'Copying...' | 'Copy selected' | 'Copied';

@Component({
  selector: 'app-rates-ladder',
  templateUrl: './rates-ladder.component.html',
  styleUrls: ['./rates-ladder.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RatesLadderComponent implements OnInit, OnDestroy {
  @Output() public openStateChanged = new EventEmitter<boolean>();
  @Input({ required: true }) public isModalOpened = false;
  @Input({ required: true }) public legComponents: LegComponent[];
  public status: Status = null;
  public form: FormGroup;
  public legIndices: string[] = [];
  public rates: Rates;
  public message: string;
  public chartOptions: AgChartOptions = {};
  public showChart = false;
  public legsFormArray: FormArray;
  public legsFormArrayControls: FormControl[];
  public selectedLegComponents: LegComponent[] = [];
  public copyState: CopyState = 'Copy selected';

  private subSink = new SubscriptionSink();
  private webSocketUUID = 'rates-ladder_' + shortUuid();
  private webSocketService = inject(GlobalWebsocketService);
  private alertsService = inject(AlertsService);
  private formBuilder = inject(FormBuilder);
  private legService = inject(LegService);

  private timeout: any;

  public ngOnInit() {
    this.form = this.formBuilder.group({
      legIndex: this.formBuilder.control('', Validators.required),
      minBumpAmount: this.formBuilder.control(-200, [
        Validators.required,
        Validators.pattern(/^-[1-9]\d*$/)
      ]),
      maxBumpAmount: this.formBuilder.control(200, [
        Validators.required,
        Validators.pattern(/^[1-9]\d*$/)
      ]),
      step: this.formBuilder.control(null, [Validators.pattern(/^[1-9]\d*$/)]),
      legsFormArray: this.formBuilder.array([])
    });

    this.legsFormArray = this.form.get('legsFormArray') as FormArray;
    this.legsFormArrayControls = this.legsFormArray.controls as FormControl[];
    this.selectedLegComponents = this.legComponents.filter(
      legComponent => legComponent.leg.checked
    );

    this.legIndices = uniq<string>(
      this.selectedLegComponents
        .map(leg => leg.form.value.floatingLegIndex)
        .filter(Boolean)
    ).sort();

    this.selectedLegComponents.forEach(_ => {
      this.legsFormArray.push(this.formBuilder.control(true));
    });

    this.form.get('legIndex').setValue(this.legIndices[0]);
    this.webSocketListener();
  }

  public ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  public modalChanged(event: boolean) {
    this.openStateChanged.emit(event);
  }

  public legToggleChange() {
    this.generateOptionsChart(this.rates);
  }

  public toggleLegs(state: boolean) {
    this.legsFormArrayControls.forEach(control => control.setValue(state));
    this.generateOptionsChart(this.rates);
  }

  public cancel(status: Status = null) {
    clearTimeout(this.timeout);
    this.status = status;
    if (status !== 'calculating') {
      this.form.enable();
    }
  }

  public calculate() {
    this.status = 'calculating';
    this.form.disable();

    const payload = {
      products: this.legService.createProductsObject(
        this.selectedLegComponents.map(legComponent => {
          return {
            product: legComponent.form.value,
            attributes: legComponent.leg.attributes
          };
        })
      ),
      legIndex: this.form.get('legIndex').value,
      minBumpAmount: this.form.get('minBumpAmount').value,
      maxBumpAmount: this.form.get('maxBumpAmount').value,
      step: this.form.get('step').value
    };

    this.rates = null;
    this.message = null;
    this.showChart = false;
    this.timeout = setTimeout(() => {
      this.cancel();
      this.alertsService.addAlert({
        message: `The rates ladder calculator took too long to respond. It may be that the
          chosen products are not eligible.`,
        type: 'error'
      });
    }, 10000);
    this.webSocketService.sendMessage({
      action: `${API_PREFIX_V2}ratesLadder`,
      payload,
      uuid: this.webSocketUUID
    });
  }

  public copy() {
    this.copyState = 'Copying...';
    copy(this.toCSV(), { format: 'text/plain' });
    setTimeout(() => {
      this.copyState = 'Copied';
    }, 1000);
    setTimeout(() => {
      this.copyState = 'Copy selected';
    }, 3000);
  }

  private toCSV() {
    const legsEnabled: boolean[] = this.legsFormArray.value;
    const ccy = this.rates.reportingCurrency;
    const header = [
      '"Bump (bps)"',
      ...Array.from(legsEnabled, (_, i) => {
        return legsEnabled[i] ? `"MtM (${ccy}) Leg ${i + 1}"` : undefined;
      }).filter(Boolean)
    ].join('\t');
    const rows = [];
    this.rates.ratesBumps.forEach((bump, i) => {
      const row = [bump];
      Object.keys(this.rates.prices).forEach((leg, legNo) => {
        if (!legsEnabled[legNo]) {
          return;
        }

        row.push(this.rates.prices[leg][i]);
      });
      rows.push(row.join('\t'));
    });
    return [header, ...rows].join('\n');
  }

  private webSocketListener(): void {
    const resubscribe = (iteration: number) => {
      this.status = null;
      if (iteration > 5) {
        this.cancel();
        this.alertsService.addAlert({
          message: 'There was a problem connecting to the ladder calculator',
          type: 'error'
        });
        return;
      }
      this.subSink.sink = this.webSocketService
        .getListener({
          action: `${API_PREFIX_V2}ratesLadder`,
          uuid: this.webSocketUUID,
          take: Infinity
        })
        .subscribe({
          next: resp => {
            clearTimeout(this.timeout);
            if (!resp.payload) {
              console.error('No payload received from rates ladder');
              return;
            }
            const message = resp.payload as Message;
            const rates = resp.payload as Rates;
            if (rates.prices) {
              this.handleRatesLadderPayload(rates);
              return;
            }
            if (message.warning) {
              this.alertsService.addAlert({
                message: message.message,
                type: 'warning'
              });
            } else if (message.info) {
              this.message = message.message;
            } else {
              this.alertsService.addAlert({
                message:
                  message.message ||
                  'The ladder calculator returned an unknown error',
                type: 'error'
              });
              this.cancel();
            }
          },
          complete: async () => {
            this.cancel();
            await sleep(exponentialDelay(iteration));
            resubscribe(iteration + 1);
          },
          error: async _ => {
            this.cancel();
            await sleep(exponentialDelay(iteration));
            resubscribe(iteration + 1);
          }
        });
    };
    resubscribe(0);
  }

  private handleRatesLadderPayload(rates: Rates) {
    this.rates = rates;
    this.generateOptionsChart(rates);
    this.cancel('finished');
  }

  private generateOptionsChart(rates: Rates): void {
    const series = [
      {
        xKey: 'x',
        yKey: 'y',
        tooltip: {
          renderer: (params: { datum: { x: number; y: number } }) => {
            const { x, y } = params.datum;
            const xs = formatNumber(+x.toPrecision(2), 'en-GB', '1.0-0');
            const ys = formatNumber(+y.toPrecision(2), 'en-GB', '1.0-0');
            return {
              content: `(${xs} bps; ${ys} ${rates.reportingCurrency})`
            };
          }
        }
      }
    ];

    // Create a pre-populated array with null y values
    const data = Array.from(rates.ratesBumps, x => {
      return { x, y: null };
    });

    const legsEnabled: boolean[] = this.legsFormArray.value;
    this.showChart = legsEnabled.some(Boolean);

    if (this.showChart) {
      Object.keys(rates.prices).forEach((leg, legNo) => {
        if (!legsEnabled[legNo]) {
          return;
        }
        rates.prices[leg].forEach((price, index) => {
          if (price != null && data[index].y == null) {
            data[index].y = price;
          } else if (price != null) {
            data[index].y += price;
          }
        });
      });
    }

    this.chartOptions = {
      background: {
        fill: 'transparent'
      },
      theme: {
        baseTheme: 'ag-default-dark',
        palette: {
          fills: AgGridColorPalette,
          strokes: AgGridColorPalette
        }
      },
      legend: {
        enabled: false,
        position: 'bottom'
      },
      data,
      series,
      axes: [
        {
          type: 'number',
          position: 'bottom',
          title: { text: 'Bump (bps)' }
        },
        {
          type: 'number',
          position: 'left',
          title: { text: `MtM (${rates.reportingCurrency})` },
          label: {
            formatter: (params: any) =>
              formatNumber(params.value, 'en-US', '1.0-0')
          }
        }
      ]
    };
  }
}
